import React, { useEffect, useState } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { fetchBoatById, cleanBoat, createBoat, updateBoat } from "store/boats"
import RemoveExperienceWithBookingsModal from "ui/RemoveExperienceWithBookingsModal"
import AddEquipmentModal from "pagesV2/pro/Equipment/AddEquipmentModal"
import { ResponsivePage } from "ui/ResponsivePage"
import { useDevice } from "hooks/useDevice"
import { useTranslation } from "react-i18next"

const EquipmentFormPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const dispatch = useDispatch()
  const { isMobile } = useDevice()
  const { t } = useTranslation()

  const isDuplicateMode = pathname.endsWith("/duplicate")
  const boatId = id ? Number(id) : null

  const boat = useSelector((state) => state.boats.boat)
  const boatLoading = boat?.loading || false

  // Edit conflict modal state
  const [editConflictModalOpen, setEditConflictModalOpen] = useState(false)
  const [editConflictExperiences, setEditConflictExperiences] = useState([])

  useEffect(() => {
    if (boatId) {
      dispatch(fetchBoatById(boatId))
    }
    return () => {
      dispatch(cleanBoat())
    }
  }, [boatId, dispatch])

  const handleCancel = () => {
    navigate(-1)
  }

  const handleSubmit = (formData, isUpdate, record) => {
    const submitPromise = isUpdate ? dispatch(updateBoat(record.id, formData)) : dispatch(createBoat(formData))

    return submitPromise.catch((error) => {
      if (error?.data?.conflicting_bookings?.length > 0) {
        const bookingsByExperience = {}

        error.data.conflicting_bookings.forEach((booking) => {
          const expName = booking.experience_name || "Unknown Experience"
          if (!bookingsByExperience[expName]) {
            bookingsByExperience[expName] = []
          }
          bookingsByExperience[expName].push({
            id: booking.booking_id,
            experienceName: expName,
            time: `${booking.start_time} - ${booking.end_time}`,
            date: booking.booked_date || "",
            imageUrl: booking.preview || "",
            location: booking.address || "",
            booking_uuid: booking.booking_uuid
          })
        })

        const conflictingExperiences = Object.keys(bookingsByExperience).map((expName) => ({
          experienceName: expName,
          bookings: bookingsByExperience[expName]
        }))

        setEditConflictExperiences(conflictingExperiences)
        setEditConflictModalOpen(true)

        const conflictError = new Error("Booking conflict")
        conflictError.isBookingConflict = true
        throw conflictError
      }
      throw error
    })
  }
  let modalTitle
  if (isDuplicateMode) {
    modalTitle = t("equipment.duplicate_equipment", { defaultValue: "Duplicate Equipment" })
  } else if (boat?.id) {
    modalTitle = t("equipment.edit_equipment", { defaultValue: "Edit Equipment" })
  } else {
    modalTitle = t("equipment.add_equipment")
  }

  return (
    <>
      <AddEquipmentModal
        visible
        onCancel={handleCancel}
        boatId={boatId}
        record={boatId ? boat : null}
        boatLoading={boatLoading}
        isDuplicateMode={isDuplicateMode}
        onSubmit={boatId ? handleSubmit : undefined}
      />

      <RemoveExperienceWithBookingsModal
        open={editConflictModalOpen}
        onCancel={() => {
          setEditConflictModalOpen(false)
          setEditConflictExperiences([])
        }}
        conflictingExperiences={editConflictExperiences}
        resourceName={boat?.name || ""}
        resourceType="equipment"
        titleKey="equipment.editEquipment.bookingConflict.title"
        infoKey="equipment.editEquipment.bookingConflict.info"
        bookingsLabelKey="equipment.editEquipment.bookingConflict.bookingsLabel"
        removeButtonKey="common.close"
      />
    </>
  )
}

export default EquipmentFormPage
